import styles from './BundleCTA.module.scss';

import cx from 'classnames';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonVariant } from '@clover-web-lib-sprout/button';

import { ContentType, ExternalLink, InternalLink } from 'api/contentful/contentTypes.cloverdotcom';
import { ButtonLinkV2, IButtonLinkV2Props } from 'components/Button/ButtonLinkV2';
import type { FlatEntry } from 'services/cms/flattenContentful';
import { Environment } from 'utils/Environment';
import { stringFormatForTestAttr } from 'utils/StringFormatForTest';
import { BUNDLE_CTA_TEST_ID } from './BundleCTA.const';
import { SproutButton } from 'components/Button/SproutButton';
import { useCartContext } from 'components/Cart/hooks/useCartContext';
import { CONNECT_TO_SALES_PATH } from 'components/App/Routes/Routes';

interface IBundleCTAProps extends Omit<IButtonLinkV2Props, 'variant' | 'isFullWidth'> {
  checkoutLink?: FlatEntry<ExternalLink>;
  dcsId?: string;
  isSelfServicePurchaseEnabled?: boolean;
  shouldUseCheckoutLink?: boolean;
  bundleCtaText?: string;
  solutionTitle?: string;
  productDcsId?: string;
  softwarePlanDcsId?: string;
  contactSalesLink?: FlatEntry<ExternalLink> | FlatEntry<InternalLink>;
}

export const BundleCTA: React.FC<IBundleCTAProps> = ({
  className,
  checkoutLink,
  contactSalesLink,
  isSelfServicePurchaseEnabled = false,
  dcsId,
  shouldUseCheckoutLink = false,
  bundleCtaText,
  solutionTitle,
  productDcsId,
  softwarePlanDcsId,
  ...props
}) => {
  const classNames = cx(className, styles.button);
  const { cart, addBundleById, setByoUpsellItems } = useCartContext();
  const { t } = useTranslation();
  const isBundleAlreadyInCart = dcsId && cart.cartMetadata?.id === dcsId;
  const hasProductAndPlanDcsIds = productDcsId && softwarePlanDcsId;
  const isProductAndPlanAlreadyInCart =
    hasProductAndPlanDcsIds &&
    productDcsId === cart.cartMetadata?.id &&
    softwarePlanDcsId === cart.softwarePlan?.id;

  const checkoutCta = (
    <ButtonLinkV2
      onClick={() => setByoUpsellItems([])}
      isFullWidth
      entry={checkoutLink}
      className={classNames}
      data-testid={BUNDLE_CTA_TEST_ID.checkoutCta}
      size="small"
      {...props}
    />
  );

  const linkEntry =
    contactSalesLink ??
    ({
      ...checkoutLink,
      contentType: ContentType.EXTERNAL_LINK,
      newTab: true,
      title: t('contact-sales'),
      url: CONNECT_TO_SALES_PATH,
    } as FlatEntry<ExternalLink>);
  const contactSalesCta = <ButtonLinkV2 isFullWidth entry={linkEntry} size="small" />;

  const buttonVariant = checkoutLink?.variant as ButtonVariant;

  if (Environment.current.isExistingMerchant || !isSelfServicePurchaseEnabled) {
    return contactSalesCta;
  }

  if (isBundleAlreadyInCart || isProductAndPlanAlreadyInCart) {
    return (
      <SproutButton disabled variant={buttonVariant} fullwidth size="small">
        {t('pricing-detail-table.checkout-cta-already-in-cart')}
      </SproutButton>
    );
  }

  if (shouldUseCheckoutLink || !(dcsId || hasProductAndPlanDcsIds)) {
    return checkoutCta;
  }

  const handleAddToCartAction = () => {
    const addOptions = { needsConfirm: true, needsRedirect: true, solutionTitle };
    if (dcsId) {
      setByoUpsellItems([]);
      addBundleById(dcsId, addOptions);
      return;
    }
  };

  return (
    <SproutButton
      onClick={handleAddToCartAction}
      className={classNames}
      variant={buttonVariant}
      fullwidth
      data-testid={stringFormatForTestAttr(`${dcsId}-add-to-cart-button`)}
      size="small"
    >
      {bundleCtaText ? bundleCtaText : t(`pricing-detail-table.add-to-cart`)}
    </SproutButton>
  );
};
